import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Upload, Save, Star, ShoppingBag, Package, X } from "lucide-react";

export default function AdminTiendaTab() {
  const [products, setProducts] = useState([]);
  const [limits, setLimits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mainRef = useRef(null);
  const extraRef = useRef(null);

  const load = useCallback(async () => {
    const [p, lim] = await Promise.all([
      api.get("/tienda/products").then((x) => x.data),
      api.get("/tienda/limits").then((x) => x.data).catch(() => null),
    ]);
    setProducts(p);
    if (lim) setLimits(lim);
    return p;
  }, []);

  useEffect(() => { load(); }, [load]);

  const atLimit = limits && limits.products_used >= limits.products_max;
  const field = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";

  const createProduct = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (!fd.get("name")) return;
    try {
      const { data } = await api.post("/tienda/products", {
        name: fd.get("name"), price: fd.get("price") || "", category: fd.get("category") || "General",
      });
      e.target.reset();
      toast.success("Producto creado");
      await load();
      setSelected(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear"); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await api.patch(`/tienda/products/${selected.id}`, {
        name: fd.get("name"), price: fd.get("price") || "", category: fd.get("category") || "General",
        description: fd.get("description") || "", status: fd.get("status"),
        highlight: fd.get("highlight") === "on",
      });
      setSelected(data);
      toast.success("Producto guardado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/tienda/products/${id}`);
    if (selected?.id === id) setSelected(null);
    toast.success("Producto eliminado");
    load();
  };

  const uploadPhoto = async (file, main) => {
    if (!file || !selected) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("main", main ? "true" : "false");
    try {
      const { data } = await api.post(`/tienda/products/${selected.id}/photo`, fd);
      setSelected(data); load();
      toast.success("Imagen subida");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
    finally {
      setUploading(false);
      if (mainRef.current) mainRef.current.value = "";
      if (extraRef.current) extraRef.current.value = "";
    }
  };

  const removePhoto = async (url) => {
    const { data } = await api.delete(`/tienda/products/${selected.id}/photo`, { params: { url } });
    setSelected(data); load();
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
      {/* Create + list */}
      <section className="lg:col-span-1">
        <form onSubmit={createProduct} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-navy font-bold"><ShoppingBag className="h-5 w-5 text-gold" /> Nuevo producto</div>
          {limits && (
            <div data-testid="products-limit" className={`mt-3 text-xs font-semibold rounded-lg px-3 py-2 ${atLimit ? "bg-[#c0392b]/10 text-[#c0392b]" : "bg-[#eef1f7] text-navy"}`}>
              {limits.products_used} / {limits.products_max} productos
            </div>
          )}
          <input name="name" data-testid="product-name-input" placeholder="Nombre del producto" className={`mt-4 ${field}`} />
          <input name="price" data-testid="product-price-input" placeholder="Precio (ej: $ 28.900)" className={`mt-3 ${field}`} />
          <input name="category" placeholder="Categoría (ej: Camisetas)" className={`mt-3 ${field}`} />
          <button data-testid="product-create-btn" disabled={atLimit} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
            <Plus className="h-4 w-4" /> Crear producto
          </button>
        </form>

        <div className="mt-5 space-y-2">
          {products.map((p) => (
            <button
              key={p.id}
              data-testid={`product-item-${p.slug}`}
              onClick={() => setSelected(p)}
              className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${selected?.id === p.id ? "bg-navy text-white border-navy" : "bg-white border-gray-100 hover:border-navy/30"}`}
            >
              <div className="h-11 w-11 rounded-lg overflow-hidden bg-[#eef1f7] shrink-0 flex items-center justify-center">
                {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <Package className="h-5 w-5 text-navy/30" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{p.name}</div>
                <div className={`text-xs ${selected?.id === p.id ? "text-gold" : "text-gray-400"}`}>{p.price} · {p.status}</div>
              </div>
            </button>
          ))}
          {products.length === 0 && <p className="text-sm text-gray-400 px-1">Todavía no hay productos.</p>}
        </div>
      </section>

      {/* Editor */}
      <section className="lg:col-span-2">
        {!selected ? (
          <div className="h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400">
            <ShoppingBag className="h-10 w-10" />
            <p className="mt-3">Seleccioná o creá un producto para editarlo</p>
          </div>
        ) : (
          <form onSubmit={saveProduct} key={selected.id} data-testid="product-editor" className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-navy font-bold"><ShoppingBag className="h-5 w-5 text-gold" /> {selected.name}</div>
              <button type="button" onClick={() => deleteProduct(selected.id)} data-testid="delete-product-btn" className="inline-flex items-center gap-1.5 text-[#c0392b] border border-[#c0392b]/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#c0392b]/5">
                <Trash2 className="h-3.5 w-3.5" /> Eliminar
              </button>
            </div>

            {/* Photos */}
            <div className="mt-4 flex flex-wrap gap-3 items-start">
              <div className="w-28 shrink-0">
                <div className="aspect-square rounded-xl overflow-hidden border border-gray-100 bg-[#eef1f7] flex items-center justify-center">
                  {selected.image_url ? <img src={selected.image_url} alt={selected.name} className="w-full h-full object-cover" /> : <Package className="h-8 w-8 text-navy/30" />}
                </div>
                <label className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-navy border border-gray-200 rounded-lg py-1.5 cursor-pointer hover:border-navy/40">
                  <Star className="h-3.5 w-3.5 text-gold" /> Principal
                  <input ref={mainRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], true)} disabled={uploading} />
                </label>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-semibold text-navy">Imágenes adicionales ({(selected.images || []).length}/3)</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(selected.images || []).map((u) => (
                    <div key={u} className="relative h-20 w-20 rounded-lg overflow-hidden border border-gray-100">
                      <img src={u} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removePhoto(u)} className="absolute top-1 right-1 h-6 w-6 rounded bg-black/50 text-white flex items-center justify-center hover:bg-[#c0392b]"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {(selected.images || []).length < 3 && (
                    <label className="h-20 w-20 rounded-lg border border-dashed border-gray-300 flex items-center justify-center cursor-pointer text-gray-400 hover:border-navy/40">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                      <input ref={extraRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], false)} disabled={uploading} />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Fields */}
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <input name="name" defaultValue={selected.name} placeholder="Nombre" className={field} />
              <input name="price" defaultValue={selected.price || ""} placeholder="Precio" className={field} />
              <input name="category" defaultValue={selected.category || ""} placeholder="Categoría" className={field} />
              <select name="status" defaultValue={selected.status || "disponible"} className={`bg-white ${field}`}>
                <option value="disponible">Disponible</option>
                <option value="agotado">Agotado</option>
              </select>
              <textarea name="description" defaultValue={selected.description || ""} rows={3} placeholder="Descripción" className={`sm:col-span-2 resize-none ${field}`} />
              <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm font-semibold text-navy">
                <input type="checkbox" name="highlight" defaultChecked={selected.highlight} data-testid="product-highlight" className="h-4 w-4 accent-navy" />
                Destacar este producto (badge dorado)
              </label>
            </div>
            <button data-testid="save-product-btn" disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar producto
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
